import React from 'react';
import { Divider, Popover, Tag, Tooltip, Typography, message } from 'antd';
import styled from 'styled-components';
import moment from 'moment';

import { useRemoveGovernanceFlagsAspectMutation } from '../../../../../graphql/mutations.generated';
import { getLocaleTimezone } from '../../../../shared/time/timeUtils';
import { useTranslation } from '../../../../../i18n/useTranslation';

const StatusTitle = styled(Typography.Text)`
    display: block;
    font-size: 14px;
    margin-bottom: 5px;
    font-weight: bold;
`;

const StatusSubTitle = styled(Typography.Text)`
    display: block;
    margin-bottom: 5px;
`;

const LastEvaluatedAtLabel = styled.div`
    padding: 0;
    margin: 0;
    display: flex;
    align-items: center;
`;

const ThinDivider = styled(Divider)`
    margin-top: 8px;
    margin-bottom: 8px;
`;

type Props = {
    urn: string;
    aspectFlag: any;
    closable?: boolean;
    refetch?: () => void;
    readOnly?: boolean;
};

/**
 * This component is used to display the governance flag batch for the entity profile page header section
 * @param urn - urn of the entity
 * @param refetch - function to refetch the data
 * @param aspectFlag - aspect flag data
 */
export const GovernanceFlagAspectBatch = ({ urn, refetch, aspectFlag, closable = true, readOnly }: Props) => {
    const { t } = useTranslation();
    const localeTimezone = getLocaleTimezone();
    const [removeGovernanceFlag] = useRemoveGovernanceFlagsAspectMutation();

    const hasDetails = aspectFlag.doc !== '';

    // Function to remove the governance flag batch from the entity
    const removeBatch = async (flag) => {
        try {
            await removeGovernanceFlag({
                variables: {
                    urn,
                    input: {
                        name: flag.name,
                    },
                },
            });
            message.destroy();
            message.success({ content: t('Flag removed successfully'), duration: 2 });
        } catch (e: unknown) {
            message.destroy();
        }
        refetch?.();
    };

    return aspectFlag?.flags.map((flag: any) => {
        // Get the user name from the actor info
        const userName = flag?.auditStamp?.actorInfo?.info?.fullName || flag?.auditStamp?.actor?.username || '';

        return (
            <Popover
                className="ml-1"
                placement="right"
                overlayStyle={{ maxWidth: 240, zIndex: 1071 }}
                content={
                    hasDetails ? (
                        <>
                            <StatusTitle>{t('Governance Aspect Batch')}</StatusTitle>
                            <ThinDivider />
                            <StatusSubTitle>{flag.doc}</StatusSubTitle>
                            <Typography.Text type="secondary">
                                <Tooltip
                                    placement="right"
                                    title={moment(flag.auditStamp.time).utc().format('dddd, DD/MMM/YYYY HH:mm:ss z')}
                                >
                                    <LastEvaluatedAtLabel className="f-color-dark-black-s70">{`Time: ${moment(
                                        flag.auditStamp.time,
                                    ).format('DD/MMM/YYYY')} (${localeTimezone})`}</LastEvaluatedAtLabel>
                                </Tooltip>
                            </Typography.Text>
                            <StatusSubTitle type="secondary">
                                {t('User')}: {userName}
                            </StatusSubTitle>
                        </>
                    ) : (
                        t('No additional details')
                    )
                }
            >
                <Tag
                    closable={closable && !readOnly}
                    className="grey-tag-light"
                    style={{ height: 'fit-content' }} // For default PreviewCard in search page
                    onClose={(e) => {
                        e.preventDefault(); // Prevent the default close action
                        if (!readOnly) {
                            removeBatch(flag);
                        }
                    }}
                >
                    {flag.name}
                </Tag>
            </Popover>
        );
    });
};
